import { useEffect, useRef } from 'react';
import { useCookieConsent } from '../../contexts/CookieContext';
import { platform } from '../../utils/platform';

declare global {
    interface Window {
        clarity?: (...args: unknown[]) => void;
    }
}

const CLARITY_ID = 'vb23ui3edw';

/**
 * Google Consent Mode command. GTM/GA only recognise a consent command when the native
 * `arguments` object is pushed (exactly what the canonical gtag.js snippet does); a plain
 * object such as `{ event: 'consent_update', ... }` is just a custom event to them.
 */
function gtagCommand(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as Record<string, unknown>);
}

const GA4_MEASUREMENT_ID = 'G-CGFPW8VXW9';

/** analytics_storage value of the Consent Mode default set in index.html (undefined if none). */
function readAnalyticsDefault(): string | undefined {
    for (const entry of window.dataLayer || []) {
        const cmd = entry as unknown as ArrayLike<unknown>;
        if (cmd && cmd[0] === 'consent' && cmd[1] === 'default') {
            return (cmd[2] as { analytics_storage?: string } | undefined)?.analytics_storage;
        }
    }
    return undefined;
}

export const CookieManager = () => {
    const { consent, hasInteracted } = useCookieConsent();
    // Page loaded with analytics denied → GTM's page-load hit was (correctly) blocked.
    const analyticsDeniedAtLoad = useRef(readAnalyticsDefault() === 'denied');
    const pageViewRecovered = useRef(false);

    // --- GOOGLE TAG MANAGER (GTM) ---
    // GTM is loaded in index.html, but we control the "consent" signal here.
    useEffect(() => {
        if (!window.dataLayer) return;

        // Map our categories to GTM consent types
        // analytics -> analytics_storage
        // marketing -> ad_storage, ad_user_data, ad_personalization
        const state = {
            ad_storage: consent.marketing ? 'granted' : 'denied',
            analytics_storage: consent.analytics ? 'granted' : 'denied',
            ad_user_data: consent.marketing ? 'granted' : 'denied',
            ad_personalization: consent.marketing ? 'granted' : 'denied',
        };

        // Real Consent Mode update (the "denied" default is set in index.html before GTM).
        // Web only, and only once the visitor has actually chosen: until then the default
        // already holds. Native shells get no default either, so nothing changes there.
        const isNativeContext = platform.isNative || new URLSearchParams(window.location.search).get('native') === 'true';
        if (hasInteracted && !isNativeContext) {
            gtagCommand('consent', 'update', state);

            // The visitor granted analytics on THIS page: send the page view that was blocked
            // at load, once. Returning visitors who had already accepted start "granted", so
            // GTM counts their page view itself and nothing is sent twice.
            if (consent.analytics && analyticsDeniedAtLoad.current && !pageViewRecovered.current) {
                pageViewRecovered.current = true;
                gtagCommand('event', 'page_view', {
                    send_to: GA4_MEASUREMENT_ID,
                    page_location: window.location.href,
                    page_title: document.title,
                });
            }
        }

        // Kept for backward compatibility with any GTM trigger listening to this event.
        window.dataLayer.push({ event: 'consent_update', consent: state });
    }, [consent, hasInteracted]);

    // --- CLARITY ---
    useEffect(() => {
        if (consent.analytics) {
            // Inject Clarity if not present
            if (!document.getElementById('clarity-script')) {
                const script = document.createElement('script');
                script.id = 'clarity-script';
                script.type = 'text/javascript';
                script.text = `
                    (function(c,l,a,r,i,t,y){
                        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                    })(window, document, "clarity", "script", "${CLARITY_ID}");
                `;
                document.head.appendChild(script);
            }
        }
    }, [consent.analytics]);

    return null; // Logic only component
};
